import { normalizeDetails, normalizeDetailsBatch } from './helpers/details_helper';
import { aggregateIdsByDetailsKey } from '../utils/details_utils';
import { componentText } from '../utils/i18n_utils';

export const ActionTypes = {
  FETCH_DETAILS_REQUEST: 'details/FETCH_DETAILS_REQUEST',
  FETCH_DETAILS_SUCCESS: 'details/FETCH_DETAILS_SUCCESS',
  FETCH_DETAILS_FAILURE: 'details/FETCH_DETAILS_FAILURE',
  REMOVE_DETAILS: 'details/REMOVE_DETAILS',
  REMOVE_ALL_DETAILS: 'details/REMOVE_ALL_DETAILS',
  REFRESH_DETAILS_BATCH_SUCCESS: 'details/REFRESH_DETAILS_BATCH_SUCCESS',
};

// The details slice is keyed [eventId][detailsKey], where detailsKey identifies
// the players of the row (see utils/details_utils.js) - aggregate ids change on
// every rescore, so they only ever appear here as URL parameters.

const controllersByKey = new Map();

// One AbortController per event for batch background refreshes
const batchControllersByEvent = new Map();

const buildAggregatesUrl = ({ aggregatesUrl, roundId }, eventId, aggIds) => {
  const queryString = aggIds.map((id) => `aggregate_ids[]=${id}`).join('&');
  return `${aggregatesUrl.replace(':round_id', roundId).replace(':id', eventId)}?${queryString}`;
};

// aggId: the row's current aggregate id, used for the request only.
export const fetchDetails = (eventId, aggId, detailsKey) => async (dispatch, getState) => {
  controllersByKey.get(detailsKey)?.abort();
  const controller = new AbortController();
  controllersByKey.set(detailsKey, controller);

  dispatch({ type: ActionTypes.FETCH_DETAILS_REQUEST, payload: { eventId, detailsKey } });

  const url = buildAggregatesUrl(getState().leaderboardV2, eventId, [aggId]);

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    if (controller.signal.aborted) return;

    controllersByKey.delete(detailsKey);

    dispatch({
      type: ActionTypes.FETCH_DETAILS_SUCCESS,
      payload: { eventId, detailsKey, data: normalizeDetails(data) },
    });
  } catch (err) {
    if (err.name !== 'AbortError') {
      dispatch({
        type: ActionTypes.FETCH_DETAILS_FAILURE,
        payload: { eventId, detailsKey, error: err.message || componentText('shared.request_failed', 'Request failed') },
      });
    }

    controllersByKey.delete(detailsKey);
  }
};

export const removeDetails = (eventId, detailsKey) => (dispatch) => {
  controllersByKey.get(detailsKey)?.abort();
  controllersByKey.delete(detailsKey);

  dispatch({
    type: ActionTypes.REMOVE_DETAILS,
    payload: { eventId, detailsKey },
  });
};

export const removeAllDetails = (eventId) => (dispatch, getState) => {
  const detailsKeys = Object.keys(getState().details?.[eventId] ?? {});
  detailsKeys.forEach((detailsKey) => {
    controllersByKey.get(detailsKey)?.abort();
    controllersByKey.delete(detailsKey);
  });

  batchControllersByEvent.get(eventId)?.abort();
  batchControllersByEvent.delete(eventId);

  dispatch({
    type: ActionTypes.REMOVE_ALL_DETAILS,
    payload: { eventId },
  });
};

// Called after a poll brought fresh results, to silently refresh every open
// scorecard of the event in one batch request. The rows' aggregate ids are
// re-resolved from the fresh event data (a rescore renews them all).
// Race conditions are handled by:
//   - Aborting any in-flight batch for the same event before starting a new one
//   - The reducer ignoring scorecards that were closed while in-flight
//   - The reducer ignoring scorecards a user-initiated fetch is loading
export const refreshOpenDetails = (eventId) => async (dispatch, getState) => {
  const state = getState();
  const detailsKeys = Object.keys(state.details?.[eventId] ?? {});
  if (detailsKeys.length === 0) return;

  const aggIds = Object.values(aggregateIdsByDetailsKey(state.event?.[eventId]?.data, detailsKeys));
  if (aggIds.length === 0) return;

  batchControllersByEvent.get(eventId)?.abort();
  const controller = new AbortController();
  batchControllersByEvent.set(eventId, controller);

  const url = buildAggregatesUrl(state.leaderboardV2, eventId, aggIds);

  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    if (controller.signal.aborted) return;

    batchControllersByEvent.delete(eventId);

    dispatch({
      type: ActionTypes.REFRESH_DETAILS_BATCH_SUCCESS,
      payload: { eventId, updates: normalizeDetailsBatch(data) },
    });
  } catch {
    batchControllersByEvent.delete(eventId);
    // Silent failure — background refresh; the next poll cycle will retry
  }
};
